import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged, getRedirectResult } from 'firebase/auth';
import { auth } from './firebase';
import Header from './components/Header';
import Hero from './sections/Hero';
import About from './sections/About';
import Features from './sections/Features';
import HowItWorks from './sections/HowItWorks';
import Testimonials from './sections/Testimonials';
import Screenshots from './sections/Screenshots';
import Availability from './sections/Availability';
import Contact from './sections/Contact';
import Footer from './components/Footer';
import ChatWidget from './components/ChatWidget';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import PoliticaPrivacidad from './pages/PoliticaPrivacidad';
import AnniversaryPage from './pages/AnniversaryPage';
import CursosVortexPage from './pages/CursosVortexPage';
import LectorTemasPreview from './pages/LectorTemasPreview';
import AnniversaryOverlay from './components/AnniversaryOverlay';
import CursosCarousel from './sections/CursosCarousel';
import { setAdminStatus } from './utils/logger';
import ToastContainer from './components/ToastContainer';

// Componente para la página principal
const LandingPage = () => (
  <>
    <AnniversaryOverlay />
    <Header />
    <Hero />
    <CursosCarousel />
    <About />
    <Features />
    <HowItWorks />
    <Testimonials />
    <Screenshots />
    <Availability />
    <Contact />
    <Footer />
    <ChatWidget />
  </>
);

// Redirección legada /cursos-vortex → /cursos preservando query params (p.ej. ?courseId=xxx)
const CursosVortexRedirect = () => {
  const location = useLocation();
  return <Navigate to={{ pathname: '/cursos', search: location.search }} replace />;
};

import AccessDenied from './components/AccessDenied';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentPending from './components/PaymentPending';
import PaymentFailure from './components/PaymentFailure';
import PurchaseLicense from './components/PurchaseLicense';


import StripeSuccess from './components/StripeSuccess';
import StripeCancel from './components/StripeCancel';
import { useAuth } from './hooks/useAuth';
import { useAdmin } from './hooks/useAdmin';
import { useUserPresence } from './hooks/useUserPresence';
import { prefetchQuizBank } from './services/rtdbCache';
import { ShadowModeProvider } from './contexts/ShadowModeContext';
import ShadowBanner from './components/ShadowBanner';

// Componente para proteger rutas premium
const PremiumProtectedRoute = ({ children }) => {
  const { user, loading, isPremium } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0a1a2a',
        color: 'white'
      }}>
        <div>Verificando licencia...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!isPremium) {
    return <Navigate to="/access-denied" replace />;
  }

  return children;
};

function AppContent() {
  const { user, loading } = useAuth();
  const { isAdmin } = useAdmin(user);
  useUserPresence();

  // Precalentar el banco de preguntas en tiempo ocioso para que la primera
  // visita a Cuestionarios/Flashcards/Debilidades ya lo encuentre en caché.
  useEffect(() => {
    if (!user) return undefined;
    const run = () => {
      prefetchQuizBank().catch(() => {});
    };
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(run, { timeout: 4000 });
      return () => window.cancelIdleCallback?.(idleId);
    }
    const timeoutId = setTimeout(run, 1500);
    return () => clearTimeout(timeoutId);
  }, [user]);

  // Sincronizar estado de admin con el logger global.
  // Cuando isAdmin cambia, se activa o silencia la consola en producción.
  useEffect(() => {
    setAdminStatus(isAdmin);
  }, [isAdmin]);

  useEffect(() => {
    const blockPrint = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        alert('Impresión deshabilitada en esta página.');
      }
    };
    window.addEventListener('keydown', blockPrint);
    return () => window.removeEventListener('keydown', blockPrint);
  }, []);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-primary)',
        color: 'var(--text-primary)'
      }}>
        <div>Cargando...</div>
      </div>
    );
  }

  return (
    <>
      <ShadowBanner />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/cursos" element={<CursosVortexPage />} />
        <Route path="/cursos-vortex" element={<CursosVortexRedirect />} />
        <Route path="/lector-preview" element={<LectorTemasPreview />} />
        <Route path="/login" element={<Login />} />
      <Route
        path="/dashboard/*"
        element={
          <PremiumProtectedRoute>
            <Dashboard />
          </PremiumProtectedRoute>
        }
      />
      <Route path="/access-denied" element={<AccessDenied />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/pending" element={<PaymentPending />} />
      <Route path="/payment/failure" element={<PaymentFailure />} />
      <Route path="/purchase-license" element={<PurchaseLicense />} />
      <Route path="/stripe-checkout" element={<Navigate to="/purchase-license" replace />} />
      <Route path="/stripe/success" element={<StripeSuccess />} />
      <Route path="/stripe/cancel" element={<StripeCancel />} />
      <Route path="/politica-privacidad" element={<PoliticaPrivacidad />} />
      <Route path="/aniversario" element={<AnniversaryPage />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <ShadowModeProvider>
        <AppContent />
        <ToastContainer />
      </ShadowModeProvider>
    </Router>
  )
}

export default App